import { useCallback, useEffect, useState } from 'react'

// Per-row decisions for one pass and one country, kept in component state and
// mirrored to localStorage.
//
// They are deliberately not in Redux: a country can carry a thousand rows, and
// running every keystroke through the root reducer would be unusable.
//
// The localStorage half is not a nicety. The server holds no decision state — a
// batch is assembled here and submitted in one go — so a refresh, a crash or a
// mis-clicked nav link would otherwise lose an hour of judgement. The TSV
// workflow this replaces had one genuinely valuable property, which was that the
// half-finished work sat in a file on disk. This is that property.
//
// Keyed by (pass, country, entityId) so a real decisions table stays a later
// addition rather than a redesign.
const storageKey = (pass, country) => `curation:${pass}:${country}`

const read = (key) => {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    // A private window, cleared site data, or storage disabled. Losing the draft
    // is survivable; failing to render the queue is not.
    console.warn('Could not read saved curation decisions', e)
    return {}
  }
}

const write = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch (e) {
    console.warn('Could not save curation decisions', e)
  }
}

export default function useDecisions(pass, country) {
  const key = storageKey(pass, country)
  const [decisions, setDecisions] = useState(() => read(key))

  // Switching country (or pass) swaps in that queue's own saved draft rather than
  // carrying the previous one across.
  useEffect(() => {
    setDecisions(read(key))
  }, [key])

  useEffect(() => {
    write(key, decisions)
  }, [key, decisions])

  const setDecision = useCallback((entityId, value) => {
    setDecisions((current) => {
      if (value === undefined || value === null) {
        const { [entityId]: _removed, ...rest } = current
        return rest
      }
      return { ...current, [entityId]: value }
    })
  }, [])

  // Called after a successful apply: those rows are decided and written, so the
  // draft is finished rather than lost.
  const clear = useCallback(() => {
    setDecisions({})
    write(key, {})
  }, [key])

  return { decisions, setDecision, clear, decidedCount: Object.keys(decisions).length }
}
